import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Typography,
} from "@material-ui/core";

import {
  Visibility,
  VisibilityOff,
  PhotoCamera,
  Close,
  PersonOutline,
  LockOutlined,
  MailOutline,
} from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import { rotuloPerfil } from "../../utils/perfil";
import ButtonBase from "@material-ui/core/ButtonBase";
import VpnKeyOutlined from "@material-ui/icons/VpnKeyOutlined";
import SecurityOutlined from "@material-ui/icons/SecurityOutlined";
import SupervisorAccountOutlined from "@material-ui/icons/SupervisorAccountOutlined";
import HeadsetMicOutlined from "@material-ui/icons/HeadsetMicOutlined";
import CheckCircleRounded from "@material-ui/icons/CheckCircleRounded";
import {
  VERDE,
  modalPapel,
  modalHero,
  modalHeroEtiqueta,
  modalHeroTitulo,
  modalHeroSub,
  modalHeroChip,
  modalFechar,
  modalCorpo,
  modalSecao,
  modalSecaoTopo,
  modalSecaoIcone,
  modalSecaoTitulo,
  modalSecaoSub,
  modalRodape,
  campoNavy,
  botaoPrimario,
  botaoSecundario,
} from "../../utils/estiloNavy";

// Perfis em cartoes (no lugar do select).
const PERFIS = [
  { valor: "admin", nome: "Administrador", desc: "Acesso total ao sistema", Icone: SecurityOutlined },
  { valor: "operador", nome: "Operador", desc: "Gerencia a equipe abaixo dele", Icone: SupervisorAccountOutlined },
  { valor: "user", nome: "Usuário comum", desc: "Atende os clientes", Icone: HeadsetMicOutlined },
];

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(255,255,255,0.10)" : "#e5ebf2";
  return {
    papel: modalPapel(theme, 600),
    hero: modalHero(theme),
    heroLinha: { display: "flex", alignItems: "center", gap: 18 },
    heroEtiqueta: modalHeroEtiqueta,
    heroTitulo: modalHeroTitulo,
    heroSub: modalHeroSub,
    heroChips: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 },
    heroChip: { ...modalHeroChip, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    fechar: { ...modalFechar, marginLeft: "auto", alignSelf: "flex-start" },
    // foto dentro do hero
    avatarRing: {
      position: "relative",
      flexShrink: 0,
      padding: 3,
      borderRadius: "50%",
      cursor: "pointer",
      background: "linear-gradient(135deg, #4ade80, #15803d)",
      boxShadow: "0 0 0 4px rgba(255,255,255,0.10), 0 10px 26px rgba(0,0,0,0.35)",
      transition: "transform .2s ease",
      "&:hover": { transform: "scale(1.04)" },
      "&:hover $avatarDica": { opacity: 1 },
    },
    avatarBig: {
      width: 78,
      height: 78,
      fontSize: "1.8rem",
      fontWeight: 800,
      color: VERDE,
      backgroundColor: "#fff",
      border: "3px solid #151d1a",
    },
    avatarDica: {
      position: "absolute",
      inset: 3,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      fontSize: 11,
      fontWeight: 800,
      background: "rgba(2,6,23,0.55)",
      opacity: 0,
      transition: "opacity .15s ease",
    },
    cameraBadge: {
      position: "absolute",
      bottom: 0,
      right: 0,
      width: 28,
      height: 28,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #22c55e, #15803d)",
      color: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "3px solid #151d1a",
    },
    corpo: modalCorpo(theme),
    secao: modalSecao(theme),
    secaoTopo: modalSecaoTopo,
    secaoIcone: modalSecaoIcone,
    secaoTitulo: modalSecaoTitulo(theme),
    secaoSub: modalSecaoSub(theme),
    campos: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      "& .MuiFormControl-marginDense": { margin: 0 },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    campoLargo: { gridColumn: "1 / -1" },
    campo: campoNavy(theme),
    inputAdornmentIcon: { color: theme.palette.text.secondary, fontSize: 19 },
    perfis: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    perfil: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 6,
      padding: "12px 12px 10px",
      borderRadius: 14,
      textAlign: "left",
      border: `1.5px solid ${borda}`,
      background: escuro ? "rgba(255,255,255,0.02)" : "#fff",
      transition: "border-color .15s, box-shadow .15s, transform .15s",
      "&:hover": { borderColor: "rgba(30,152,85,0.5)", transform: "translateY(-1px)" },
    },
    perfilAtivo: {
      borderColor: VERDE,
      background: escuro ? "rgba(30,152,85,0.12)" : "#f0fbf4",
      boxShadow: "0 0 0 3px rgba(30,152,85,0.12)",
    },
    perfilIcone: {
      width: 30,
      height: 30,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: VERDE,
      background: "rgba(30,152,85,0.12)",
      "& svg": { fontSize: 17 },
    },
    perfilNome: { fontSize: "0.86rem", fontWeight: 800, color: theme.palette.text.primary },
    perfilDesc: { fontSize: "0.72rem", color: theme.palette.text.secondary, lineHeight: 1.3 },
    perfilMarca: { position: "absolute", top: 8, right: 8, color: VERDE, fontSize: 18 },
    rodape: { ...modalRodape(theme), justifyContent: "flex-end" },
    btnPrimario: botaoPrimario,
    btnSecundario: botaoSecundario(theme),
    dica: { fontSize: "0.74rem", color: theme.palette.text.secondary, marginTop: 8 },
  };
});

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Nome muito curto!")
    .max(50, "Nome muito longo!")
    .required("Nome é obrigatório"),
  password: Yup.string().min(5, "A senha deve ter no mínimo 5 caracteres").max(50, "Senha muito longa"),
  email: Yup.string().email("E-mail inválido").required("E-mail é obrigatório"),
});

const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const initialState = {
    name: "",
    email: "",
    password: "",
    profile: "user",
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  // A foto de perfil e exibida em circulos de 40 a 56 pixels. Subir o arquivo
  // original da camera (varios MB) para mostrar isso e o que fazia as telas com
  // avatar demorarem a carregar. 256px de lado ja e o dobro do maior uso, e o
  // arquivo cai para dezenas de KB.
  const reduzirImagem = (arquivo, ladoMaximo = 256) =>
    new Promise((resolve) => {
      if (!arquivo || !arquivo.type || !arquivo.type.startsWith("image/")) {
        return resolve(arquivo);
      }

      const endereco = URL.createObjectURL(arquivo);
      const imagem = new Image();

      imagem.onload = () => {
        try {
          const maior = Math.max(imagem.width, imagem.height);

          // Ja e pequena: nao mexe. Reencodar de graca so perderia qualidade.
          if (maior <= ladoMaximo) {
            URL.revokeObjectURL(endereco);
            return resolve(arquivo);
          }

          const escala = ladoMaximo / maior;
          const largura = Math.round(imagem.width * escala);
          const altura = Math.round(imagem.height * escala);

          const tela = document.createElement("canvas");
          tela.width = largura;
          tela.height = altura;
          tela.getContext("2d").drawImage(imagem, 0, 0, largura, altura);

          tela.toBlob(
            (blob) => {
              URL.revokeObjectURL(endereco);
              if (!blob) return resolve(arquivo);
              resolve(
                new File([blob], "perfil.jpg", {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                })
              );
            },
            "image/jpeg",
            0.9
          );
        } catch (e) {
          // Deu qualquer problema no canvas: manda o original. Foto grande e
          // melhor que foto nenhuma.
          URL.revokeObjectURL(endereco);
          resolve(arquivo);
        }
      };

      imagem.onerror = () => {
        URL.revokeObjectURL(endereco);
        resolve(arquivo);
      };

      imagem.src = endereco;
    });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reduzida = await reduzirImagem(file);
      setSelectedFile(reduzida);
      setPreview(URL.createObjectURL(reduzida));
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => ({
          ...prevState,
          ...data,
        }));
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchUser();
    }
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setSelectedFile(null);
    setPreview(null);
    setShowPassword(false);
  };

  const handleSaveUser = async (values) => {
    // whatsappId vai sempre null: usuario nao pertence a conexao nenhuma.
    // Salvar um usuario antigo tambem limpa o vinculo que sobrou no banco.
    const userData = {
      ...values,
      whatsappId: null,
    };

    try {
      let savedUserId = userId;
      if (userId) {
        await api.put(`/users/${userId}`, userData);
      } else {
        const { data } = await api.post("/users", userData);
        savedUserId = data.id;
      }

      if (selectedFile && savedUserId) {
        const formData = new FormData();
        formData.append("profileImage", selectedFile);
        await api.post(`/users/${savedUserId}/profile-image`, formData);
      }

      toast.success(i18n.t("userModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const currentAvatarSrc =
    preview ||
    (user.profileImage ? `${getBackendUrl()}public/${user.profileImage}` : undefined);

  const openFilePicker = () => fileInputRef.current && fileInputRef.current.click();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ className: classes.papel }}
    >
      <Formik
        initialValues={user}
        enableReinitialize={true}
        validationSchema={UserSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveUser(values);
            actions.setSubmitting(false);
          }, 200);
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue }) => (
          <Form style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
            {/* topo navy com a foto */}
            <div className={classes.hero}>
              <div className={classes.heroLinha}>
                <div className={classes.avatarRing} onClick={openFilePicker} title="Alterar foto de perfil">
                  <Avatar className={classes.avatarBig} src={currentAvatarSrc}>
                    {values.name ? values.name.charAt(0).toUpperCase() : <PersonOutline style={{ fontSize: 40 }} />}
                  </Avatar>
                  <span className={classes.avatarDica}>Trocar</span>
                  <div className={classes.cameraBadge}>
                    <PhotoCamera style={{ fontSize: 14 }} />
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className={classes.heroEtiqueta}>Equipe</div>
                  <div className={classes.heroTitulo}>
                    {userId ? values.name || "Editar usuário" : "Novo usuário"}
                  </div>
                  {userId ? (
                    <div className={classes.heroChips}>
                      {values.email && (
                        <span className={classes.heroChip}>
                          <MailOutline /> {values.email}
                        </span>
                      )}
                      <span className={classes.heroChip}>
                        <SecurityOutlined /> {rotuloPerfil(values.profile)}
                      </span>
                    </div>
                  ) : (
                    <div className={classes.heroSub}>Clique na foto para escolher uma imagem de perfil.</div>
                  )}
                </div>
                <IconButton size="small" className={classes.fechar} onClick={handleClose}>
                  <Close fontSize="small" />
                </IconButton>
              </div>
            </div>

            <div className={classes.corpo}>
              {/* Acesso */}
              <div className={classes.secao}>
                <div className={classes.secaoTopo}>
                  <span className={classes.secaoIcone}><VpnKeyOutlined /></span>
                  <div>
                    <div className={classes.secaoTitulo}>Dados de acesso</div>
                    <div className={classes.secaoSub}>Nome, e-mail de login e senha.</div>
                  </div>
                </div>
                <div className={classes.campos}>
                  <Field
                    as={TextField}
                    label={i18n.t("userModal.form.name")}
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={`${classes.campo} ${classes.campoLargo}`}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutline className={classes.inputAdornmentIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("userModal.form.email")}
                    name="email"
                    autoComplete="off"
                    inputProps={{ autoComplete: "off" }}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.campo}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutline className={classes.inputAdornmentIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Field
                    as={TextField}
                    name="password"
                    variant="outlined"
                    size="small"
                    // Sem isto o Chrome preenche a senha salva de quem esta
                    // logado (o admin) e ela vira a senha do usuario editado.
                    autoComplete="new-password"
                    inputProps={{ autoComplete: "new-password" }}
                    label={userId ? "Nova senha" : i18n.t("userModal.form.password")}
                    placeholder={userId ? "Deixe em branco para manter" : ""}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    className={classes.campo}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined className={classes.inputAdornmentIcon} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((e) => !e)}
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>

              {/* Perfil */}
              <div className={classes.secao} style={{ animationDelay: "60ms" }}>
                <div className={classes.secaoTopo}>
                  <span className={classes.secaoIcone}><SecurityOutlined /></span>
                  <div>
                    <div className={classes.secaoTitulo}>{i18n.t("userModal.form.profile")}</div>
                    <div className={classes.secaoSub}>O que este usuário pode fazer no sistema.</div>
                  </div>
                </div>
                <Can
                  role={loggedInUser.profile}
                  perform="user-modal:editProfile"
                  yes={() => (
                    <div className={classes.perfis}>
                      {PERFIS.map((p) => {
                        const ativo = values.profile === p.valor;
                        return (
                          <ButtonBase
                            key={p.valor}
                            className={`${classes.perfil} ${ativo ? classes.perfilAtivo : ""}`}
                            onClick={() => setFieldValue("profile", p.valor)}
                          >
                            {ativo && <CheckCircleRounded className={classes.perfilMarca} />}
                            <span className={classes.perfilIcone}><p.Icone /></span>
                            <span className={classes.perfilNome}>{p.nome}</span>
                            <span className={classes.perfilDesc}>{p.desc}</span>
                          </ButtonBase>
                        );
                      })}
                    </div>
                  )}
                  no={() => (
                    <TextField
                      label={i18n.t("userModal.form.profile")}
                      value={rotuloPerfil(user.profile)}
                      variant="outlined"
                      size="small"
                      disabled
                      fullWidth
                      className={classes.campo}
                    />
                  )}
                />
                {/*
                  O seletor de "Conexao Padrao (WhatsApp)" foi removido de
                  proposito: todo usuario atende em todas as conexoes.
                */}
              </div>
            </div>

            <div className={classes.rodape}>
              <Button onClick={handleClose} disabled={isSubmitting} className={classes.btnSecundario}>
                {i18n.t("userModal.buttons.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting} className={classes.btnPrimario}>
                {isSubmitting && <CircularProgress size={16} style={{ color: "#fff", marginRight: 8 }} />}
                {userId ? i18n.t("userModal.buttons.okEdit") : i18n.t("userModal.buttons.okAdd")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UserModal;
